// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/Content/InternetView.js

import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Chips } from 'primereact/chips';
import { FloatLabel } from 'primereact/floatlabel';
import ToastNotification from '../ToastNotification';
import { v4 as uuidv4 } from 'uuid';

const InternetView = ({
  promotions,
  setPromotions,
  totalCount,
  searchQuery,
  pageLength,
  currentPage,
  setCurrentPage,
  handlePageLengthChange,
  handleInsertPromotion,
  handleUpdatePromotion,
  handleDeletePromotion,
  refreshData,
}) => {
  const [editingRows, setEditingRows] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const first = (currentPage - 1) * pageLength;

  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const refresh = () => {
    if (shouldRefresh) {
      if (typeof refreshData === 'function') {
        refreshData();
      } else {
        console.warn('refreshData prop not provided; skipping full data refresh');
        setPromotions((prev) => [...prev]);
      }
      setShouldRefresh(false);
    }
  };

  const featuresBodyTemplate = (rowData) => {
    return rowData?.features?.join(', ') || '';
  };

  const idBodyTemplate = (rowData) => {
    return rowData?.id || '';
  };

  const textEditor = (options) => {
    return (
      <InputText
        type="text"
        value={options.value || ''}
        onChange={(e) => options.editorCallback(e.target.value)}
        style={{ width: '100%' }}
      />
    );
  };

  const featuresEditor = (options) => {
    return (
      <FloatLabel>
        <Chips
          id="features"
          value={options.value || []}
          onChange={(e) => options.editorCallback(e.value)} // อัปเดตค่าเมื่อ Chips เปลี่ยน
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              const inputValue = e.target.value.trim();
              if (inputValue) {
                const newValues = [...(options.value || []), inputValue];
                options.editorCallback(newValues);
                e.target.value = ''; // ล้าง input หลังกด Enter
              }
              e.preventDefault(); // ป้องกันการ submit form
            }
          }}
          allowDuplicate={false} // ป้องกันการเพิ่มค่า重复
          style={{ width: '100%' }}
        />
        <label htmlFor="features">Features</label>
      </FloatLabel>
    );
  };

  const onRowEditComplete = async (e) => {
    const { newData, index } = e;
    if (!newData || !newData.id) {
      setEditingRows({});
      return;
    }

    try {
      if (!newData.title || !newData.price || !newData.code) {
        throw new Error('Title, price, and code are required');
      }
      await handleUpdatePromotion(newData);
      setPromotions((prev) => {
        const updatedPromotions = [...prev];
        return updatedPromotions;
      });
      showToastMessage(`Promotion ID ${newData.id} updated successfully`, 'success');
    } catch (error) {
      console.error('Error updating promotion:', error);
      showToastMessage(`Error updating promotion: ${error.message}`, 'error');
      setShouldRefresh(true);
    } finally {
      refresh();
      setEditingRows({});
    }
  };

  const deleteActionTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-danger p-button-sm"
        onClick={async () => {
          try {
            await handleDeletePromotion(rowData.id);
            setPromotions((prev) => prev.filter((promo) => promo.id !== rowData.id));
            showToastMessage('Promotion deleted successfully', 'success');
          } catch (error) {
            console.error('Error deleting promotion:', error);
            showToastMessage(`Error deleting promotion: ${error.message}`, 'error');
            setShouldRefresh(true);
          } finally {
            refresh();
          }
        }}
      />
    );
  };

  const addPromotion = async () => {
    const newPromotion = {
      id: 0,
      title: "New Promotion",
      price: "99.99",
      features: ["features 1", "features 2"],
      code: `PROMO-${uuidv4().slice(0, 8)}`,
    };

    try {
      if (!newPromotion.title || !newPromotion.price || !newPromotion.code) {
        throw new Error('Title, price, and code are required');
      }
      const response = await handleInsertPromotion(newPromotion);
      if (response && response.id) {
        const insertedPromo = { ...newPromotion, id: response.id };
        setPromotions((prev) => [...prev, insertedPromo]);
        showToastMessage('Promotion added successfully', 'success');
      } else {
        throw new Error('No ID returned from server');
      }
    } catch (error) {
      console.error('Error adding promotion:', error);
      showToastMessage(`Error adding promotion: ${error.message}`, 'error');
      setShouldRefresh(true);
    } finally {
      refresh();
      const updatedTotal = totalCount + 1;
      const lastPage = Math.ceil(updatedTotal / pageLength);
      setCurrentPage(lastPage);
    }
  };

  const filteredPromotions = promotions.filter((promotion) =>
    promotion &&
    ((promotion.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (promotion.price || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (promotion.code || '').toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <>
      <ToastNotification
        showToast={showToast}
        toastMessage={toastMessage}
        toastVariant={toastVariant}
        setShowToast={setShowToast}
      />
      <div className="d-grid gap-2 d-md-flex justify-content-md-end">
        <div className="select-container me-md-2">
          <select
            id="pageLengthSelect"
            className="form-select"
            value={pageLength}
            onChange={handlePageLengthChange}
          >
            <option value="10">10 Rows</option>
            <option value="20">20 Rows</option>
            <option value="50">50 Rows</option>
          </select>
        </div>
      </div>

      <DataTable
        value={filteredPromotions}
        paginator
        rows={pageLength}
        totalRecords={totalCount}
        lazy
        first={first}
        onPage={(e) => setCurrentPage(Math.floor(e.first / pageLength) + 1)}
        dataKey="id"
        editMode="row"
        editingRows={editingRows}
        onRowEditComplete={onRowEditComplete}
        tableStyle={{ minWidth: '50rem' }}
        className="p-datatable-gridlines"
      >
        <Column field="id" header="ID" sortable body={idBodyTemplate}  />
        <Column field="title" header="Title" sortable editor={textEditor} />
        <Column field="price" header="Price" sortable editor={textEditor} />
        <Column
          field="features"
          header="Features"
          sortable
          body={featuresBodyTemplate}
          editor={featuresEditor}
        />
        <Column field="code" header="Code" sortable editor={textEditor} />
        <Column rowEditor headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }} />
        <Column
          header="Delete"
          body={deleteActionTemplate}
          headerStyle={{ width: '10%', minWidth: '8rem' }}
          bodyStyle={{ textAlign: 'center' }}
        />
      </DataTable>

      <div className="text-right mt-2">
        <Button
          icon="pi pi-plus"
          label="Add Promotion"
          className="p-button-success p-button-sm"
          onClick={addPromotion}
        />
      </div>
    </>
  );
};

export default InternetView;